import { apyToAprPercent } from '../../../utils/parsing'
import { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Tokenized RWA money-market / treasury funds — the DefiLlama-mapped
 * batch (2026-08 sweep). All are **permissioned-mint** (KYC at the
 * issuer, category D in ASSET_ACQUISITION.md) so — with one exception —
 * none get a savings registry row: like USDY/OUSG they are integrated
 * data-side only, the intrinsic-yield keys below serving the lending
 * join (`intrinsicYields[asset.assetGroup]`) wherever a lender lists
 * them as collateral, plus the global intrinsic map. The exception is
 * cUSDO, whose *wrapper* is permissionless — it additionally has
 * savings-registry rows binding the same keys (see its entry).
 *
 * Keys are the **verified** token-list `assetGroup` strings. Several
 * are split across chains (the chain-1 list truncates/strips names —
 * same defect class as the Ondo groups), so every *anchored* variant
 * is emitted. Variants deliberately NOT emitted, to avoid blessing an
 * impostor with a yield: `Re al US T Bill::USTB` (fake), `BUIDL
 * 404::BUIDL` + `dfohub::buidl` (unrelated), `Benji Bean::BENJI`
 * (meme), `cUSDO Ethereum::cUSDO` (unverified deployment).
 *
 * Rates come from each fund's DefiLlama pool (`apy`, percent,
 * identical across a fund's chain deployments) → converted to the
 * package-wide nominal APR. Per-fund failures degrade to that fund
 * being absent, not to 0 — a dead pool must not zero a live market's
 * carry.
 *
 * NOT here, and why:
 *  - WTGXX (WisdomTree) + BENJI (Franklin) — in our lists but no
 *    DefiLlama pool → no rate source yet.
 *  - STBT (Matrixdock) — has a pool but is absent from our lists.
 *  - deUSD/sdeUSD (Elixir) — winding down post-Stream; xUSD (Stream)
 *    — collapsed. Do not add.
 *  - wUSDM (Mountain) — declining/sunsetting, no live pool found.
 */

interface RwaFund {
  /** DefiLlama pool UUID (one per fund — the largest deployment). */
  pool: string
  /** Token-list assetGroup variants to emit, copied character-for-character. */
  keys: string[]
}

const RWA_FUNDS: { [label: string]: RwaFund } = {
  // Circle (ex-Hashnote) USYC — $3.0B, the largest tokenized MMF.
  // Base's `US Yield Coin::USYC` (0xeee7af83…) is unverified → skipped.
  USYC: {
    pool: '7c0a89c7-70cf-460c-b62e-cb278bf97e8f',
    keys: ['Hashnote USYC::USYC', 'Circle USYC::USYC'],
  },
  // BlackRock BUIDL — $2.0B. Rebasing (NAV $1, monthly dividend
  // distributions); the percent is still the holder's carry. Chain-1
  // group is the truncated variant.
  BUIDL: {
    pool: 'b663ca59-c7e6-4435-ae4a-28d339ce6a15',
    keys: [
      'BlackRock USD Institutional Digital Liquidity Fund::BUIDL',
      'BlackRock USD Institutional Digital Liq::BUIDL',
    ],
  },
  // Superstate USTB — $740M short-duration govt securities.
  USTB: {
    pool: '1910847a-f8b5-40ce-a1ab-1dafdded5fbb',
    keys: ['Superstate Short Duration US Government Securities Fund::USTB'],
  },
  // Superstate USCC — crypto-carry fund (basis, not T-bills).
  USCC: {
    pool: 'aff74ce8-4fe3-462b-af11-542cc16d24b2',
    keys: ['Superstate USCC::USCC', 'Superstate Crypto Carry Fund::USCC'],
  },
  // VanEck VBILL (via Securitize) — $179M.
  VBILL: {
    pool: '5663e2ef-7e9c-4755-9701-9b59b90dbba3',
    keys: ['VanEck Treasury Fund::VBILL'],
  },
  // OpenEden TBILL — bare group on 1/42161. (THBILL is Theo's fund —
  // a different asset with its own fetcher.)
  TBILL: {
    pool: 'e140f3b2-0327-46ea-93f5-88b17b0a0a16',
    keys: ['TBILL'],
  },
  // OpenEden cUSDO — compounding ERC-4626 wrapper over USDO; accrues
  // USDO's T-bill yield. The sweep's one full-vault graduate: it also
  // has savings-registry rows on 1/56/8453 (permissionless wrap —
  // `maxDeposit = uint256.max` verified) binding these same keys.
  CUSDO: {
    pool: 'f083596e-032d-4d6b-a7a8-1836d3f99bcd',
    keys: [
      'Compounding Open Dollar::CUSDO',
      'Compounding Open Dollar::cUSDO',
      'Compounding OpenDollar::CUSDO',
    ],
  },
}

export const rwaFundsFetcher: YieldFetcher = {
  label: 'RWA_FUNDS',
  fetch: async () => {
    const out: { [key: string]: number } = {}
    await Promise.all(
      Object.entries(RWA_FUNDS).map(async ([label, fund]) => {
        try {
          const apr = apyToAprPercent(await fetchDefiLlamaApy(fund.pool))
          for (const key of fund.keys) out[key] = apr
        } catch {
          // absent, not zero — see the header note
        }
      }),
    )
    return out
  },
}

/** Exported for tests. */
export const RWA_FUND_TABLE = RWA_FUNDS
